import React, { useState } from 'react';
import { 
  Box, 
  Card, 
  Title, 
  Text, 
  Group, 
  Button, 
  Badge, 
  List, 
  Stack, 
  Textarea,
  Transition,
  Spoiler 
} from '@mantine/core';
import { IconEdit, IconCheck } from '@tabler/icons-react';
import AIGeneratingIcon from '../components/AIGeneratingIcon';

const NarrativePanel = ({ 
  visible, 
  unifiedNarrative, 
  isLoading = false,
  onEdit,
  onSave,
  onCancel,
  onRegenerate,
  cardHeight = '600px'
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editText, setEditText] = useState('');

  const handleEditStart = () => {
    setEditText(unifiedNarrative?.narrative || '');
    setIsEditing(true);
    onEdit?.();
  };

  const handleSave = () => {
    onSave?.(editText);
    setIsEditing(false);
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditText('');
    onCancel?.();
  };

  const hasNarrative = unifiedNarrative?.narrative;

  return (
    <Transition mounted={visible} transition="slide-left" duration={300} timingFunction="ease">
      {(styles) => (
        <Stack style={styles} spacing="md">
          <Card 
            withBorder 
            radius="md" 
            p="xl"
            sx={{ 
              background: '#f0f8ff', 
              height: cardHeight,
              display: 'flex',
              flexDirection: 'column'
            }}
          >
            <Stack spacing="md" sx={{ height: '100%' }}>
              
              {/* Header */}
              <Group position="apart" align="center">
                <Title order={4} color="blue.7">
                  🎯 Model Narrative
                </Title>
                <Badge 
                  color={isLoading ? 'blue' : hasNarrative ? 'green' : 'gray'} 
                  variant="light"
                >
                  {isLoading ? 'Generating...' : hasNarrative ? 'Generated' : 'Not Generated'}
                </Badge>
              </Group>

              {/* Loading State */}
              {isLoading && (
                <Box sx={{ textAlign: 'center', py: 'xl' }}>
                  <AIGeneratingIcon isGenerating={true} size={48} />
                  <Text mt="md" color="blue.7" weight={500}>
                    Generating unified narrative...
                  </Text>
                  <Text size="sm" color="dimmed">
                    Analyzing all context sections
                  </Text>
                </Box>
              )}

              {/* Generated Narrative */}
              {hasNarrative && !isLoading && (
                <Stack spacing="md" sx={{ flex: 1 }}>
                  
                  {/* Controls */}
                  <Group spacing="xs" position="left">
                    <Button
                      size="xs"
                      variant="light"
                      color="gray"
                      leftSection={<IconEdit size={14} />}
                      onClick={handleEditStart}
                      disabled={isEditing}
                    >
                      Edit
                    </Button>
                    <Button
                      size="xs"
                      variant="light"
                      color="blue"
                      leftSection={<AIGeneratingIcon isGenerating={isLoading} size={14} />}
                      onClick={onRegenerate}
                      disabled={isLoading || isEditing}
                    >
                      {isLoading ? 'Regenerating...' : 'Regenerate'}
                    </Button>
                  </Group>

                  {/* Key Characteristics - TOP */}
                  {unifiedNarrative?.keyCharacteristics && unifiedNarrative.keyCharacteristics.length > 0 && (
                    <Card withBorder p="md" sx={{ borderLeft: '3px solid #228be6' }}>
                      <Text size="sm" weight={500} mb="xs" color="blue.7" align="left">
                        Key Characteristics:
                      </Text>
                      <Group spacing="xs" position="left">
                        {unifiedNarrative.keyCharacteristics.map((char, idx) => (
                          <Badge key={idx} size="sm" variant="light" color="blue">
                            {char}
                          </Badge>
                        ))}
                      </Group>
                    </Card>
                  )}

                  {/* Main Narrative Content */}
                  <Card withBorder p="md" sx={{ flex: 1, borderLeft: '3px solid #51cf66' }}>
                    {isEditing ? (
                      <Stack spacing="sm">
                        <Textarea
                          value={editText}
                          onChange={(e) => setEditText(e.target.value)}
                          minRows={15}
                          placeholder="Edit the unified narrative..."
                          sx={{
                            fontSize: '14px',
                            lineHeight: 1.6,
                            '& .mantine-Textarea-input': {
                              textAlign: 'left'
                            }
                          }}
                        />
                        <Group spacing="xs" position="left">
                          <Button
                            size="xs"
                            color="green"
                            leftSection={<IconCheck size={14} />}
                            onClick={handleSave}
                          >
                            Save
                          </Button>
                          <Button
                            size="xs"
                            variant="light"
                            color="gray"
                            onClick={handleCancel}
                          >
                            Cancel
                          </Button>
                        </Group>
                      </Stack>
                    ) : (
                      <Box>
                        <Text size="sm" weight={500} mb="xs" color="green.7" align="left">
                          Narrative Content:
                        </Text>
                        <Spoiler maxHeight={300} showLabel="Show more" hideLabel="Show less">
                          <Text 
                            size="sm" 
                            style={{ 
                              whiteSpace: 'pre-wrap', 
                              textAlign: 'left',
                              lineHeight: 1.6 
                            }} 
                            align="left"
                          >
                            {unifiedNarrative.narrative}
                          </Text>
                        </Spoiler>
                      </Box>
                    )}
                  </Card>

                  {/* Confidence Score */}
                  {unifiedNarrative?.confidence !== undefined && (
                    <Card withBorder p="sm" sx={{ borderLeft: '3px solid #fab005' }}>
                      <Group spacing="xs" position="left">
                        <Text size="sm" weight={500} color="yellow.7">
                          Confidence Score:
                        </Text>
                        <Badge size="sm" color="yellow" variant="light">
                          {Math.round(unifiedNarrative.confidence * 100)}%
                        </Badge>
                      </Group>
                    </Card>
                  )}

                  {/* Missing Information - BOTTOM */}
                  {unifiedNarrative?.missingInfo && unifiedNarrative.missingInfo.length > 0 && (
                    <Card withBorder p="md" sx={{ borderLeft: '3px solid #fa5252' }}>
                      <Text size="sm" weight={500} mb="xs" color="red.7" align="left">
                        Missing Information:
                      </Text>
                      <List 
                        size="sm" 
                        spacing="xs" 
                        sx={{ 
                          '& .mantine-List-item': { 
                            textAlign: 'left',
                            color: '#fa5252'
                          } 
                        }}
                      >
                        {unifiedNarrative.missingInfo.map((info, idx) => (
                          <List.Item key={idx} style={{ textAlign: 'left' }}>
                            {info}
                          </List.Item>
                        ))}
                      </List>
                    </Card>
                  )}
                </Stack>
              )}

              {/* Empty State */}
              {!hasNarrative && !isLoading && (
                <Box sx={{ textAlign: 'center', py: 'xl', color: '#868e96' }}>
                  <Text size="lg" weight={500} mb="xs">
                    No Narrative Generated
                  </Text>
                  <Text size="sm">
                    Complete all context sections and click "Generate Complete Narrative" to see the unified narrative here.
                  </Text>
                </Box>
              )}
            </Stack>
          </Card>
          
          {/* Spacer to match left panel's generate button height */}
          <Box sx={{ height: '60px' }} />
        </Stack>
      )}
    </Transition>
  );
};

export default NarrativePanel;